/**
 * A Kusto address — a pasted string's Data Source (or its alias, or the URI it starts with) — read completely, or refused
 * with a secret-free reason (Option C C3 reviews r2 C3-11 and r3 C3-14 / C3-17).
 *
 * The scheme and host are the cluster; one path segment is the database (`https://help.kusto.windows.net/Samples`); an
 * App Insights / Log Analytics proxy keeps its whole path. Anything else is refused BEFORE a path is read: quotes or
 * braces (the Kusto client has no quoting, so a quoted address holds property text the client would split off — C3-14),
 * `;` / `=` property text, whitespace, a query, a fragment, a user name, a deeper path, or a URL that does not parse.
 * A proxy is recognised on the parsed URL, so an explicit `:443` is the same proxy (C3-17).
 */
import { isKustoProxyUrl, normalizeKustoClusterUrl } from '@/core/engines/kustoUrl';
import { safeDecode } from './helpers';

const KUSTO_HOST = /(^|\.)(kusto(mfa)?\.(windows\.net|data\.microsoft\.com|fabric\.microsoft\.com|azuresynapse\.net|usgovcloudapi\.net|chinacloudapi\.cn))$/iu;
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//iu;

/** Why a Kusto string is refused (shown as "Can't be used as it is: …"); fixed text, never a piece of the input. */
export const KUSTO_ADDRESS_PROBLEMS = {
  quoted: 'the cluster URL is in quotes or braces, which the Kusto client does not treat as quoting',
  properties: 'the cluster URL holds connection-string properties',
  extras: 'the cluster URL carries a query, a fragment or a user name',
  path: 'the cluster URL has more than a database after the host',
  invalid: 'the cluster URL is not a valid URL',
  cutValue: 'a value holds "=", which the Kusto client cuts off',
  cutSecret: 'a secret ends with "/" or "\\", which the Kusto client cuts off',
  quotedValue: 'a value holds a quote or brace, which the Kusto client does not treat as quoting',
  unreadable: 'the Kusto client cannot read it (a keyword it does not know, an empty item, or a flag with no value)',
} as const;

export type KustoProblem = string;

/** The quote problem naming the character (the r5 ruling: a refusal says which character). */
export function kustoQuoteProblem(character: string, address: boolean): KustoProblem {
  return `${address ? 'the cluster URL' : 'a value'} holds ${character} — the Kusto client does not treat quotes or braces as quoting`;
}

/** The host of a URL or bare `host[:port][/path]`, past any `user:password@` (that is refused later, never kept). */
function hostOf(source: string): string {
  return source.trim().replace(/^[a-z]+:\/\//iu, '').replace(/^[^/?#]*@/u, '').split(/[/:?#]/u)[0] ?? '';
}

/** A Kusto cluster, a Fabric eventhouse, or an App Insights / Log Analytics query proxy. */
export function isKustoHost(source: string): boolean {
  return KUSTO_HOST.test(hostOf(source)) || isKustoProxyUrl(source);
}

/** An http(s) URL or a Kusto host — never a SQL Server address. */
export function isKustoLikeAddress(value: string): boolean {
  return /^https?:\/\//iu.test(value.trim()) || isKustoHost(value);
}

export function startsWithScheme(text: string): boolean {
  return SCHEME.test(text.trim());
}

export function kustoAddress(raw: string): { server: string; database?: string } | { problem: KustoProblem } {
  const text = raw.trim();
  const quote = /["'{}]/u.exec(text);
  if (quote) return { problem: kustoQuoteProblem(quote[0], true) };
  if (/[;=]/u.test(text)) return { problem: KUSTO_ADDRESS_PROBLEMS.properties };
  if (/[\s?#@]/u.test(text)) return { problem: KUSTO_ADDRESS_PROBLEMS.extras };
  const withScheme = SCHEME.test(text) ? text : `https://${text}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return { problem: KUSTO_ADDRESS_PROBLEMS.invalid };
  }
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || !url.hostname) return { problem: KUSTO_ADDRESS_PROBLEMS.invalid };
  if (isKustoProxyUrl(withScheme)) return { server: normalizeKustoClusterUrl(withScheme) };
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments.length > 1) return { problem: KUSTO_ADDRESS_PROBLEMS.path };
  const server = normalizeKustoClusterUrl(`${url.protocol}//${url.host}`);
  return segments[0] ? { server, database: safeDecode(segments[0]) } : { server };
}
